import React from 'react';
import {WorkItemType} from '../../../../shared/dtos/estimate-tree.dto';
import treePin from '../../../../assets/tree_pin.png';
import treeGroupPin from '../../../../assets/tree_group_pin.png';
import fallenTreePin from '../../../../assets/fallen_tree_pin.png';
import stumpPin from '../../../../assets/stump_pin.png';
import debrisPilePin from '../../../../assets/debris_pile_pin.png';
import miscellaneousPin from '../../../../assets/miscellaneous_pin.png';
import truckChipperArt from '../../../../assets/truck_chipper_icon.png';
import craneArt from '../../../../assets/crane_icon.png';
import {CHIPPER_ASPECT, CRANE_ART_ASPECT, CRANE_TURRET_FRACTION} from './map-geometry';
import {MARKER_DIAMETER_PX} from './map-spiderfy';

/*
 * The job site map's artwork (SQ-621), drawn as SVG in screen pixels: the
 * port of Android's MapMarkerIcons.kt, shape for shape and colour for colour.
 * Every piece is drawn about its own anchor; the caller places and turns it.
 */

export {truckChipperArt, craneArt};

export const COLORS = {
  gate: '#161616',
  gateOutline: '#FFFFFF',
  gateNumberInk: '#161616',
  chipper: '#C62828',
  dragPath: '#D32F2F',
  leader: '#FF0000',
  craneLine: '#2F8CFF',
  craneLineEdge: '#0A2540',
  crane: '#F2C200',
  craneOn: '#1F1A00',
  handleArrow: '#FF2E2E',
  handleRim: '#7A0D0D',
  live: '#4CAF50',
};

export const workItemLabel = (type: WorkItemType): string => {
  switch (type) {
    case WorkItemType.Tree: return 'Tree';
    case WorkItemType.TreeGroup: return 'Tree Group';
    case WorkItemType.FallenTree: return 'Fallen Tree';
    case WorkItemType.Stump: return 'Stump';
    case WorkItemType.DebrisPile: return 'Debris Pile';
    case WorkItemType.Misc: return 'Miscellaneous';
    default: return 'Work Item';
  }
};

const pinFor = (type: WorkItemType): string => {
  switch (type) {
    case WorkItemType.TreeGroup: return treeGroupPin;
    case WorkItemType.FallenTree: return fallenTreePin;
    case WorkItemType.Stump: return stumpPin;
    case WorkItemType.DebrisPile: return debrisPilePin;
    case WorkItemType.Misc: return miscellaneousPin;
    default: return treePin;
  }
};

/** Shared definitions: the text shadow every white number carries. */
export const ArtDefs = () => (
  <defs>
    <filter id="sm-text-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="1" stdDeviation="1.2" floodColor="#000" floodOpacity="0.95"/>
    </filter>
    <filter id="sm-soft-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="1" stdDeviation="1.4" floodColor="#000" floodOpacity="0.4"/>
    </filter>
  </defs>
);

/** A work item's pin: the type's picture clipped to a 38 px circle, its number in white on top. */
export const WorkItemPin = ({id, x, y, type, label}: {id: number; x: number; y: number; type: WorkItemType; label: string}) => {
  const d = MARKER_DIAMETER_PX;
  const r = d / 2;
  const pad = Math.max(2, d * 0.08);
  const clip = `sm-pin-clip-${id}`;
  return (
    <g transform={`translate(${x} ${y})`}>
      <clipPath id={clip}><circle cx={0} cy={0} r={r}/></clipPath>
      <circle cx={0} cy={pad * 0.4} r={r} fill="rgba(0,0,0,0.4)"/>
      {/* The circle takes the top of the taller pin picture, as Android's clip does. */}
      <image href={pinFor(type)} x={-r} y={-r} width={d} height={d} preserveAspectRatio="xMidYMin slice" clipPath={`url(#${clip})`}/>
      {label && (
        <text x={0} y={-d * 0.12} dy="0.35em" textAnchor="middle" fontWeight={700} fontSize={Math.max(10, d * 0.28)}
              fill="#fff" filter="url(#sm-text-shadow)">{label}</text>
      )}
    </g>
  );
};

/** The red arrow from the pin's edge down to the work item's real spot. */
export const LeaderArrow = ({bx, by, tx, ty}: {bx: number; by: number; tx: number; ty: number}) => {
  const angle = Math.atan2(ty - by, tx - bx) * 180 / Math.PI + 90;
  const w = 7;
  const h = 7.7;
  return (
    <g>
      <line x1={bx} y1={by} x2={tx} y2={ty} stroke={COLORS.leader} strokeWidth={2}/>
      {/* Android's cap: pointing along the line, its point on the spot. */}
      <path transform={`translate(${tx} ${ty}) rotate(${angle})`} d={`M0 0 L${w / 2} ${h} L0 ${h * 0.72} L${-w / 2} ${h} Z`} fill={COLORS.leader}/>
    </g>
  );
};

/** The truck & chipper picture, front up, anchored at the back of the chipper and turned there. */
export const TruckArt = ({x, y, lengthPx, heading}: {x: number; y: number; lengthPx: number; heading: number}) => {
  const width = lengthPx * CHIPPER_ASPECT;
  return (
    <image href={truckChipperArt} x={-width / 2} y={-lengthPx} width={width} height={lengthPx} preserveAspectRatio="none"
           transform={`translate(${x} ${y}) rotate(${heading})`}/>
  );
};

/**
 * The crane picture, anchored and turned at its turret. The asset is drawn
 * with the cab to the right; turned a quarter back it is front up like the
 * truck's, as Android turns it.
 */
export const CraneArt = ({x, y, artPx, heading}: {x: number; y: number; artPx: number; heading: number}) => {
  const width = artPx * CRANE_ART_ASPECT;
  return (
    <g transform={`translate(${x} ${y}) rotate(${heading}) rotate(-90)`}>
      <image href={craneArt} x={-CRANE_TURRET_FRACTION * artPx} y={-width / 2} width={artPx} height={width} preserveAspectRatio="none"/>
    </g>
  );
};

/** A machine's number, always upright: white with a dark shadow over the artwork. */
export const MachineNumber = ({x, y, size, label}: {x: number; y: number; size: number; label: string}) => (
  <text x={x} y={y} dy="0.35em" textAnchor="middle" fontWeight={700} fontSize={size} fill="#fff" filter="url(#sm-text-shadow)">{label}</text>
);

/** Half the arc of the half moon either side of the machine's nose. */
export const HANDLE_HALF_ANGLE_DEG = 56;
const HALF = HANDLE_HALF_ANGLE_DEG * Math.PI / 180;

const onArc = (angle: number, r: number) => [Math.cos(angle) * r, Math.sin(angle) * r];
const arcPath = (r: number, from: number, to: number) => {
  const [x0, y0] = onArc(from, r);
  const [x1, y1] = onArc(to, r);
  return `M${x0} ${y0} A${r} ${r} 0 ${to - from > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
};

/** The half moon's white band, pointing north about its arc's centre: also its grab area, stroked wider. */
export const halfMoonBand = (radius: number) => arcPath(radius, -Math.PI / 2 - HALF, -Math.PI / 2 + HALF);

/** The white half moon with two red arrows, drawn about its arc's centre and turned to the heading. */
export const HalfMoon = ({x, y, radius, thickness, heading}: {x: number; y: number; radius: number; thickness: number; heading: number}) => {
  const north = -Math.PI / 2;
  const a0 = north - HALF;
  const a1 = north + HALF;
  const head = 0.5;
  const arrow = (edge: number, dir: number) => {
    const [tx, ty] = onArc(edge + dir * 0.067, radius);
    const [b1x, b1y] = onArc(edge + dir * head, radius * 1.213);
    const [b2x, b2y] = onArc(edge + dir * head, radius * 0.787);
    return `M${tx} ${ty} L${b1x} ${b1y} L${b2x} ${b2y} Z`;
  };
  return (
    <g transform={`translate(${x} ${y}) rotate(${heading})`}>
      <path d={halfMoonBand(radius)} fill="none" stroke="#fff" strokeWidth={thickness} strokeLinecap="round" filter="url(#sm-soft-shadow)"/>
      <path d={arcPath(radius, a0 + head, a1 - head)} fill="none" stroke={COLORS.handleArrow} strokeWidth={radius * (2.6 / 15)} strokeLinecap="round"/>
      <path d={arrow(a0, 1)} fill={COLORS.handleArrow}/>
      <path d={arrow(a1, -1)} fill={COLORS.handleArrow}/>
    </g>
  );
};

// ---- Gates (SQ-527, SQ-286) ------------------------------------------------------------------------------

const GATE_UNIT = 39 / 60;
const leaf = (x0: number, xMid: number) =>
  `M${x0} 17 Q${(x0 + xMid) / 2 + (xMid > x0 ? 0.6 : -0.6)} 16 ${xMid} 11 L${xMid} 37 L${x0} 37 Z`;
const PICKETS: [number, number][] = [[14, 16.6], [18.5, 16], [23, 14.8], [37, 14.8], [41.5, 16], [46, 16.6]];

/** The double gate, drawn in its 60 x 40 units with the gate's centre at (30, 20). */
const GateDrawing = () => (
  <>
    {[true, false].map(halo => {
      const color = halo ? COLORS.gateOutline : COLORS.gate;
      const grow = halo ? 2.6 : 0;
      const solid = {fill: color, stroke: color, strokeWidth: 0.6 + grow, strokeLinejoin: 'round' as const};
      const line = (w: number) => ({fill: 'none', stroke: color, strokeWidth: w + grow, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const});
      return (
        <g key={String(halo)}>
          <rect x={2} y={12} width={6} height={27} rx={1} {...solid}/>
          <rect x={52} y={12} width={6} height={27} rx={1} {...solid}/>
          <circle cx={5} cy={8.5} r={3.2} {...solid}/>
          <circle cx={55} cy={8.5} r={3.2} {...solid}/>
          <path d={leaf(9.5, 28.8)} {...line(2.4)}/>
          <path d={leaf(50.5, 31.2)} {...line(2.4)}/>
          {PICKETS.map(([px, top]) => <line key={px} x1={px} y1={top} x2={px} y2={37} {...line(1.8)}/>)}
          <line x1={9.5} y1={27} x2={28.8} y2={27} {...line(1.6)}/>
          <line x1={31.2} y1={27} x2={50.5} y2={27} {...line(1.6)}/>
        </g>
      );
    })}
  </>
);

/** The gate on the map, at its fixed 39 px width, turned to its heading about its centre. */
export const GateArt = ({x, y, heading}: {x: number; y: number; heading: number}) => (
  <g transform={`translate(${x} ${y}) rotate(${heading}) scale(${GATE_UNIT}) translate(-30 -20)`}><GateDrawing/></g>
);

/** The gate's picture for menus and panels, `width` px wide. */
export const GateGlyph = ({width}: {width: number}) => (
  <svg width={width} height={width * 45 / 64} viewBox="0 0 64 45" aria-hidden><g transform="translate(2 3)"><GateDrawing/></g></svg>
);

/** The gate's number: a white bubble ringed in near-black, upright at every heading. */
export const GateNumber = ({x, y, label}: {x: number; y: number; label: string}) => {
  const textPx = MARKER_DIAMETER_PX * 0.28 * 0.75;
  const radius = Math.max(textPx * 0.78, label.length * textPx * 0.3 + 1.7);
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={radius + 0.8} fill={COLORS.gateNumberInk}/>
      <circle r={radius} fill="#fff"/>
      <text dy="0.35em" textAnchor="middle" fontWeight={700} fontSize={textPx} fill={COLORS.gateNumberInk}>{label}</text>
    </g>
  );
};

/** The gate's turn handle: a 9 px disc 22 px off the gate's centre along its heading. */
export const GATE_TURN_HANDLE_RADIUS = 9;
export const GATE_TURN_HANDLE_DISTANCE = 22;

export const GateTurnHandle = ({x, y, heading}: {x: number; y: number; heading: number}) => {
  const r = GATE_TURN_HANDLE_RADIUS;
  const rr = r * 0.5;
  const start = 150 * Math.PI / 180;
  const end = 420 * Math.PI / 180;
  const [sx, sy] = onArc(start, rr);
  const [ex, ey] = onArc(end, rr);
  const rx = Math.cos(end);
  const ry = Math.sin(end);
  const head = rr * 0.8;
  return (
    <g transform={`translate(${x} ${y}) rotate(${heading}) translate(0 ${-GATE_TURN_HANDLE_DISTANCE})`}>
      <circle r={r * 1.12} fill="rgba(0,0,0,0.4)"/>
      <circle r={r} fill="#fff"/>
      <path d={`M${sx} ${sy} A${rr} ${rr} 0 1 1 ${ex} ${ey}`} fill="none" stroke={COLORS.gateNumberInk} strokeWidth={r * 0.16} strokeLinecap="round"/>
      <path d={`M${ex - ry * head} ${ey + rx * head} L${ex + rx * head * 0.75} ${ey + ry * head * 0.75} L${ex - rx * head * 0.75} ${ey - ry * head * 0.75} Z`}
            fill={COLORS.gateNumberInk}/>
    </g>
  );
};

// ---- Cranes (SQ-527) -------------------------------------------------------------------------------------

/** A staging pile's size, which its grab area matches. */
export const PILE_PX = 42;

/** The staging pile: a white disc ringed in the reach line's blue, a heap of brush inside. */
export const StagingPile = ({x, y}: {x: number; y: number}) => {
  const k = PILE_PX / (2 * 17.35);
  const ink = (w: number) => ({stroke: COLORS.craneLineEdge, strokeWidth: w, strokeLinecap: 'round' as const, fill: 'none'});
  return (
    <g transform={`translate(${x} ${y}) scale(${k}) translate(-17 -17)`}>
      <circle cx={17} cy={17} r={17.35} fill={COLORS.craneLineEdge}/>
      <circle cx={17} cy={17} r={16.75} fill={COLORS.craneLine}/>
      <circle cx={17} cy={17} r={14.25} fill="#fff"/>
      <line x1={6} y1={22} x2={28} y2={22} {...ink(2.6)}/>
      <path d="M8 22 C10 18 14 16 17 16" {...ink(2.2)}/>
      <path d="M26 22 C24 17 19 15 15 16" {...ink(2.2)}/>
      <line x1={11} y1={15} x2={15} y2={18} {...ink(2.2)}/>
      <line x1={22} y1={13} x2={18} y2={17} {...ink(2.2)}/>
      <line x1={17} y1={10} x2={17} y2={15} {...ink(2.2)}/>
      <line x1={13} y1={12} x2={15} y2={14} {...ink(2)}/>
      <line x1={21} y1={16} x2={23} y2={14} {...ink(2)}/>
    </g>
  );
};

/** The crane's reach: blue over a dark edge. Dashed, it is the swing line to the pile. */
export const CraneLine = ({x1, y1, x2, y2, dashed}: {x1: number; y1: number; x2: number; y2: number; dashed?: boolean}) => (
  <g>
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={COLORS.craneLineEdge} strokeWidth={5} strokeDasharray={dashed ? '9 6' : undefined}/>
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={COLORS.craneLine} strokeWidth={3} strokeDasharray={dashed ? '9 6' : undefined}/>
  </g>
);

/** "38 ft 4 in" in a thin pill laid along the reach line, never upside down. */
export const ReachPill = ({x, y, rotation, text}: {x: number; y: number; rotation: number; text: string}) => {
  const size = 10;
  const width = text.length * size * 0.58 + size;
  const height = size + size * 0.44;
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotation})`}>
      <rect x={-width / 2} y={-height / 2} width={width} height={height} rx={height / 2} fill="#fff" stroke={COLORS.craneLine} strokeWidth={0.9}/>
      <text dy="0.35em" textAnchor="middle" fontWeight={700} fontSize={size} fill={COLORS.craneLineEdge}>{text}</text>
    </g>
  );
};

/** A live route's grab point: a clean white dot with a dark rim. */
export const RouteHandle = ({x, y}: {x: number; y: number}) => (
  <circle cx={x} cy={y} r={6} fill="#fff" stroke={COLORS.handleRim} strokeWidth={6 * 0.36} filter="url(#sm-soft-shadow)"/>
);
